import type { ApiClient } from 'grabbitbot-legacy-ui'
import type {
  AdminRecord,
  CandleSeries,
  Coin,
  CoinPairList,
  ClosedPriceCandleResponse,
  ClosedPricePoint,
  IndicatorCondition,
  IndicatorData,
  IndicatorDataItem,
  IndicatorList,
  Momentum,
  MomentumAlert,
  ServiceAccount,
  Status,
  TextData,
} from './types'

const MOMENTUMS_PATTERN = /^\/momentums/
const COINPAIRLISTS_PATTERN = /^\/coinpairlists/
const INDICATORCONDITIONS_PATTERN = /^\/indicatorconditions/
const TEXTDATA_PATTERN = /^\/textdata/
const SERVICE_ACCOUNTS_PATTERN = /^\/serviceAccounts/

/**
 * Screener-domain API client — port of the legacy `CoinService`,
 * `CandleService`, `IndicatorService`, `MomentumService`,
 * `CoinpairlistService`, `IndicatorconditionService`, `TextdataService`,
 * `ServiceAccountService`, `StatusService`, `EventLogService` and
 * `AdminService` (`src/app/data/api-client/*.service.ts`, audit §4). All
 * paths match `api.cryptopairscreener.com/docs/legacy-contract/routes.json`
 * exactly — see this app's README "Endpoints used not in routes.json" for
 * the two exceptions this repo could not avoid (dead legacy
 * `/dashboard/statistics` and `/status/*`, both present in the contract but
 * unreachable from any ported route, so they're simply not called here).
 */
export function createScreenerClient(client: ApiClient) {
  return {
    // --- coins -----------------------------------------------------------
    searchCoins: () => client.get<Coin[]>('/coins'),
    searchClosedCoins: () => client.get<Coin[]>('/coins/closed'),

    // --- candles / closed price -------------------------------------------
    // UNVERIFIED + dead code (no page calls these) — see the doc comments on
    // `CandleSeries`/`ClosedPricePoint` in ./types for why the coinscreener
    // sibling's real wire shape can't be confirmed yet.
    getCandles: (exchangeId: string, tradingType: string, coin1: string, coin2: string, interval: string) =>
      client.get<CandleSeries>('/candles/{exchangeId}/{tradingType}/{coin1}/{coin2}/{interval}', {
        params: { exchangeId, tradingType, coin1, coin2, interval },
      }),

    getClosedPrice: (exchangeId: string, tradingType: string, coin1: string, coin2: string, interval: string) =>
      client.get<ClosedPricePoint[]>('/closedprice/{exchangeId}/{tradingType}/{coin1}/{coin2}/{interval}', {
        params: { exchangeId, tradingType, coin1, coin2, interval },
      }),

    // The external candle.grabbitbot.com microservice's shape (confirmed
    // live) — an OBJECT with a `data` array, not a bare point array. See
    // `parseClosedPriceCandleSeries` (./closedPriceCandle) for the adapter
    // `CoinSheetPage` uses to turn this into chartable points.
    getClosedPriceFromCandle: (
      exchangeId: string,
      tradingType: string,
      coin1: string,
      coin2: string,
      interval: string,
      count: number,
    ) =>
      client.get<ClosedPriceCandleResponse>('/closedpricecandle/{exchangeId}/{tradingType}/{coin1}/{coin2}/{interval}', {
        params: { exchangeId, tradingType, coin1, coin2, interval },
        query: { count },
      }),

    getClosedPriceFromCandleLive: (
      exchangeId: string,
      tradingType: string,
      coin1: string,
      coin2: string,
      interval: string,
      count: number,
    ) =>
      client.get<ClosedPriceCandleResponse>(
        '/closedpricecandle/{exchangeId}/{tradingType}/{coin1}/{coin2}/{interval}/live',
        { params: { exchangeId, tradingType, coin1, coin2, interval }, query: { count }, cache: false },
      ),

    // --- indicators --------------------------------------------------------
    searchIndicators: (exchangeId: string, tradingType: string, coin1: string, coin2: string) =>
      client.get<IndicatorList>('/indicators/{exchangeId}/{tradingType}/{coin1}/{coin2}', {
        params: { exchangeId, tradingType, coin1, coin2 },
      }),

    searchClosedIndicators: (exchangeId: string, tradingType: string, coin1: string, coin2: string) =>
      client.get<IndicatorList>('/indicators/{exchangeId}/{tradingType}/{coin1}/{coin2}/closedprice', {
        params: { exchangeId, tradingType, coin1, coin2 },
      }),

    // UNVERIFIED + dead code (no page calls either) — wrapper shape is from
    // the legacy `IndicatorData` model class, see ./types.
    viewIndicator: (
      exchangeId: string,
      tradingType: string,
      coin1: string,
      coin2: string,
      indicator: string,
      interval: string,
    ) =>
      client.get<IndicatorData>('/indicators/{exchangeId}/{tradingType}/{coin1}/{coin2}/{indicator}/{interval}', {
        params: { exchangeId, tradingType, coin1, coin2, indicator, interval },
      }),

    viewClosedIndicator: (
      exchangeId: string,
      tradingType: string,
      coin1: string,
      coin2: string,
      indicator: string,
      interval: string,
    ) =>
      client.get<IndicatorData>(
        '/indicators/{exchangeId}/{tradingType}/{coin1}/{coin2}/{indicator}/{interval}/closedprice',
        { params: { exchangeId, tradingType, coin1, coin2, indicator, interval } },
      ),

    // Legacy `calcIndicator(): Observable<IndicatorDataItem<null|number>[]>`
    // — a bare array (unlike view*Indicator's wrapped object above).
    calcIndicator: (indicator: string, data: unknown) =>
      client.post<IndicatorDataItem[]>('/indicators/{indicator}/calc', data, { params: { indicator } }),

    // --- momentums -----------------------------------------------------
    searchMomentums: (exchangeId: string, tradingType: string) =>
      client.get<Momentum[]>('/momentums/{exchangeId}/{tradingType}', { params: { exchangeId, tradingType } }),

    createMomentum: (exchangeId: string, tradingType: string, data: Partial<Momentum>) =>
      client.post<Momentum>('/momentums/{exchangeId}/{tradingType}', data, {
        params: { exchangeId, tradingType },
        invalidates: [MOMENTUMS_PATTERN],
      }),

    viewMomentum: (exchangeId: string, tradingType: string, id: string) =>
      client.get<Momentum>('/momentums/{exchangeId}/{tradingType}/{id}', { params: { exchangeId, tradingType, id } }),

    modifyMomentum: (exchangeId: string, tradingType: string, id: string, data: Partial<Momentum>) =>
      client.patch<Momentum>('/momentums/{exchangeId}/{tradingType}/{id}', data, {
        params: { exchangeId, tradingType, id },
        invalidates: [MOMENTUMS_PATTERN],
      }),

    deleteMomentum: (exchangeId: string, tradingType: string, id: string) =>
      client.delete<unknown>('/momentums/{exchangeId}/{tradingType}/{id}', {
        params: { exchangeId, tradingType, id },
        invalidates: [MOMENTUMS_PATTERN],
      }),

    searchMomentumAlerts: (
      momentumId: string,
      exchangeId: string,
      tradingType: string,
      coin1: string,
      coin2: string,
      interval: string,
      count = 250,
    ) =>
      client.get<MomentumAlert[]>(
        '/momentums/{momentumId}/alerts/{exchangeId}/{tradingType}/{coin1}/{coin2}/{interval}',
        { params: { momentumId, exchangeId, tradingType, coin1, coin2, interval }, query: { count } },
      ),

    // --- coin-pair lists -----------------------------------------------
    searchCoinpairlists: (exchangeId: string, tradingType: string) =>
      client.get<CoinPairList[]>('/coinpairlists/{exchangeId}/{tradingType}', { params: { exchangeId, tradingType } }),

    createCoinpairlist: (exchangeId: string, tradingType: string, data: Partial<CoinPairList>) =>
      client.post<CoinPairList>('/coinpairlists/{exchangeId}/{tradingType}', data, {
        params: { exchangeId, tradingType },
        invalidates: [COINPAIRLISTS_PATTERN],
      }),

    viewCoinpairlist: (exchangeId: string, tradingType: string, id: string) =>
      client.get<CoinPairList>('/coinpairlists/{exchangeId}/{tradingType}/{id}', {
        params: { exchangeId, tradingType, id },
      }),

    modifyCoinpairlist: (exchangeId: string, tradingType: string, id: string, data: Partial<CoinPairList>) =>
      client.patch<CoinPairList>('/coinpairlists/{exchangeId}/{tradingType}/{id}', data, {
        params: { exchangeId, tradingType, id },
        invalidates: [COINPAIRLISTS_PATTERN],
      }),

    deleteCoinpairlist: (exchangeId: string, tradingType: string, id: string) =>
      client.delete<unknown>('/coinpairlists/{exchangeId}/{tradingType}/{id}', {
        params: { exchangeId, tradingType, id },
        invalidates: [COINPAIRLISTS_PATTERN],
      }),

    searchCoinpairlistMomentums: (exchangeId: string, tradingType: string, id: string) =>
      client.get<Momentum[]>('/coinpairlists/{exchangeId}/{tradingType}/{id}/momentums', {
        params: { exchangeId, tradingType, id },
      }),

    // --- indicator conditions -------------------------------------------
    searchIndicatorconditions: (exchangeId: string, tradingType: string) =>
      client.get<IndicatorCondition[]>('/indicatorconditions/{exchangeId}/{tradingType}', {
        params: { exchangeId, tradingType },
      }),

    createIndicatorcondition: (exchangeId: string, tradingType: string, data: Partial<IndicatorCondition>) =>
      client.post<IndicatorCondition>('/indicatorconditions/{exchangeId}/{tradingType}', data, {
        params: { exchangeId, tradingType },
        invalidates: [INDICATORCONDITIONS_PATTERN],
      }),

    viewIndicatorcondition: (exchangeId: string, tradingType: string, id: string) =>
      client.get<IndicatorCondition>('/indicatorconditions/{exchangeId}/{tradingType}/{id}', {
        params: { exchangeId, tradingType, id },
      }),

    modifyIndicatorcondition: (exchangeId: string, tradingType: string, id: string, data: Partial<IndicatorCondition>) =>
      client.patch<IndicatorCondition>('/indicatorconditions/{exchangeId}/{tradingType}/{id}', data, {
        params: { exchangeId, tradingType, id },
        invalidates: [INDICATORCONDITIONS_PATTERN],
      }),

    deleteIndicatorcondition: (exchangeId: string, tradingType: string, id: string) =>
      client.delete<unknown>('/indicatorconditions/{exchangeId}/{tradingType}/{id}', {
        params: { exchangeId, tradingType, id },
        invalidates: [INDICATORCONDITIONS_PATTERN],
      }),

    searchIndicatorconditionMomentums: (exchangeId: string, tradingType: string, id: string) =>
      client.get<Momentum[]>('/indicatorconditions/{exchangeId}/{tradingType}/{id}/momentums', {
        params: { exchangeId, tradingType, id },
      }),

    // --- text data --------------------------------------------------------
    searchTextdata: () => client.get<TextData[]>('/textdata'),
    createTextdata: (data: Partial<TextData>) =>
      client.post<TextData>('/textdata', data, { invalidates: [TEXTDATA_PATTERN] }),
    viewTextdata: (id: string) => client.get<TextData>('/textdata/{id}', { params: { id } }),
    modifyTextdata: (id: string, data: Partial<TextData>) =>
      client.patch<TextData>('/textdata/{id}', data, { params: { id }, invalidates: [TEXTDATA_PATTERN] }),
    deleteTextdata: (id: string) =>
      client.delete<unknown>('/textdata/{id}', { params: { id }, invalidates: [TEXTDATA_PATTERN] }),

    // --- service accounts -------------------------------------------------
    searchServiceAccountRoles: () => client.get<string[]>('/serviceAccounts/roles'),
    searchServiceAccounts: () => client.get<ServiceAccount[]>('/serviceAccounts'),
    addServiceAccount: (data: Partial<ServiceAccount>) =>
      client.post<ServiceAccount>('/serviceAccounts', data, { invalidates: [SERVICE_ACCOUNTS_PATTERN] }),
    viewServiceAccount: (id: string) => client.get<ServiceAccount>('/serviceAccounts/{id}', { params: { id } }),
    viewServiceAccountPassword: (id: string) =>
      client.get<{ password: string }>('/serviceAccounts/{id}/pass', { params: { id }, cache: false }),
    modifyServiceAccount: (id: string, data: Partial<ServiceAccount>) =>
      client.patch<ServiceAccount>('/serviceAccounts/{id}', data, {
        params: { id },
        invalidates: [SERVICE_ACCOUNTS_PATTERN],
      }),
    revokeServiceAccount: (id: string) =>
      client.delete<unknown>('/serviceAccounts/{id}', { params: { id }, invalidates: [SERVICE_ACCOUNTS_PATTERN] }),

    // --- exchange / bot status ---------------------------------------------
    viewBotStatus: () => client.get<Status>('/status/bot'),
    checkBotStatus: () => client.post<Status>('/status/bot/check', undefined, { invalidates: [/^\/status\/bot/] }),
    viewExchangeStatus: () => client.get<Status>('/status/exchange'),
    checkExchangeStatus: () =>
      client.post<Status>('/status/exchange/check', undefined, { invalidates: [/^\/status\/exchange/] }),

    // --- telemetry beacon ---------------------------------------------------
    // Legacy `EventLogService.event()` fired on nearly every page's
    // `ngOnInit` (audit §4). Kept as fire-and-forget, non-blocking, and
    // never awaited by callers — a failed beacon must never break a page.
    logEvent: (type: string, action: string, data?: unknown) =>
      client.post<unknown>('/log/event', { type, action, data }, { cache: false }).catch(() => undefined),

    // --- admin (BUSINESS-CRITICAL, audit §4) --------------------------------
    admin: {
      searchEventLog: (query: {
        date?: string
        from?: number
        to?: number
        email?: string
        pageSize?: number
        page?: number
        order?: string
        hideAlg?: boolean
      }) => client.get<AdminRecord>('/admin/log/events', { query, cache: false }),

      listUsers: () => client.get<AdminRecord[]>('/admin/users'),

      hourlyUsage: () => client.get<AdminRecord>('/admin/statistics/usage/hourly', { cache: false }),

      userHourlyUsage: (email: string) =>
        client.get<AdminRecord>('/admin/statistics/user/usage/hourly', { query: { email }, cache: false }),

      viewCredit: (email: string, currency: string) =>
        client.get<AdminRecord>('/admin/credit', { query: { email, currency }, cache: false }),

      addCredit: (data: { email: string; currency: string; amount: number; cause: string; reference?: string }) =>
        client.post<AdminRecord>('/admin/credit/add', data, { invalidates: [/^\/admin\/credit/] }),

      removeCredit: (data: { email: string; currency: string; amount: number; cause: string; reference?: string }) =>
        client.post<AdminRecord>('/admin/credit/remove', data, { invalidates: [/^\/admin\/credit/] }),
    },
  }
}

export type ScreenerClient = ReturnType<typeof createScreenerClient>
